import React, { Fragment, useEffect, useState } from 'react'

import { Stack, Typography } from '@mui/material_modern'
import { Box, Button, InputLabel, useTheme } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material_modern'

import { useFormContext } from 'react-hook-form'
import { showFormModal } from 'state/ui'
import { useDispatch } from 'react-redux'
import { get } from 'lodash'
import Input from 'components/generic/form/Input'
import FileUploader from 'components/generic/file-uploader/FileUploader'
import { getImageUrl } from 'services/assets'
import { DT_STATUS_ENUM } from 'utils/constants'
import { getDayHourSecondString } from 'components/containers/load-board/utils/helperFunctions'

interface CompletionInfoFormRowProps {
  dispatchTicketData: any
}

const CompletionInfoFormRow: React.FC<CompletionInfoFormRowProps> = ({ dispatchTicketData }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { setValue, watch, control, formState } = useFormContext()
  const backgroundColor =
    dispatchTicketData?.status === DT_STATUS_ENUM.COMPLETED ? 'primary.lightBg' : 'error.background'
  const ticketStatus = watch('status')
  const completionNote = watch('completionNote')
  const [signatureUrl, setSignatureUrl] = useState('')

  useEffect(() => {
    if (dispatchTicketData?.signature) {
      getImageUrl(dispatchTicketData.signature).then(url => setSignatureUrl(url))
    } else {
      setSignatureUrl('')
    }
  }, [dispatchTicketData?.signature])

  const renderSingleField = settings => {
    const { sx, InputLabelProps } = settings
    const standardFieldSettings = {
      ...(sx && { sx }),
      ...(InputLabelProps && { InputLabelProps }),
      key: settings?.name,
      name: settings?.name,
      label: settings?.label,
      size: settings?.size || 'small',
      variant: settings?.variant || 'outlined',
      'data-test': settings?.dataTest,
      control: control,
      error: !!get(formState?.errors, settings?.name),
      helperText: get(formState.errors, `${settings?.name}.message`),
      defaultValue: settings?.defaultValue || (settings?.fieldType === 'customerAutocomplete' ? null : ''),
      onBlur: settings?.onBlur,
      onChange: settings?.onChange,
      disabled: !!settings?.disabled,
    }

    if (settings.name === 'signature' && signatureUrl) {
      return (
        <Box sx={{ flex: 2 }}>
          <InputLabel sx={{ color: theme.palette.text.secondary }} shrink>
            {settings.label}
          </InputLabel>
          <img
            src={signatureUrl}
            alt="Signature"
            style={{
              maxWidth: '200px',
              marginTop: '8px',
            }}
          />
        </Box>
      )
    } else {
      return (
        <Input
          {...standardFieldSettings}
          variant="standard"
          disabled
          multiline
          maxRows={4}
          sx={{
            '& .MuiInputBase-input': {
              scrollbarWidth: '2px',
              '&::-webkit-scrollbar': {
                width: '2px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            },
            'input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
            'label.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.secondary,
            },
            'textarea.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
              whiteSpace: 'normal',
              overflow: 'visible',
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      )
    }
  }

  useEffect(() => {
    if (dispatchTicketData) {
      const durationString = getDayHourSecondString(dispatchTicketData.startTime, dispatchTicketData.completionTime)
      setValue('duration', durationString)
      if (dispatchTicketData.mileage === '') {
        setValue('mileage', '--')
      }
      setValue(
        'completionNote',
        dispatchTicketData.driverCompletionNotes?.trim() ||
          dispatchTicketData.dispatchTicketAssets[0]?.completionNote?.trim() ||
          '--',
      )
      setValue(
        'dispatcherCompletionNote',
        dispatchTicketData.dispatcherCompletionNotes?.trim() ||
          dispatchTicketData.dispatchTicketAssets[0]?.dispatcherCompletionNote?.trim() ||
          '--',
      )
    }
  }, [dispatchTicketData, ticketStatus, completionNote])

  const fields = [
    {
      name: 'duration',
      label: 'Total Duration',
      dataTest: 'total-duration-test-id',
      fieldType: 'static',
      defaultValue: '--',
      sx: { flex: 2 },
    },
    {
      name: 'mileage',
      label: 'Mileage',
      dataTest: 'mileage-test-id',
      fieldType: 'static',
      defaultValue: '--',
      sx: { flex: 2 },
    },
    {
      name: 'completionNote',
      label: 'Driver Notes',
      dataTest: 'driver-notes-test-id',
      fieldType: 'static',
      defaultValue: '--',
      sx: { flex: 2 },
    },
    {
      name: 'dispatcherCompletionNote',
      label: 'Dispatcher Notes',
      dataTest: 'dispatcher-notes-test-id',
      fieldType: 'static',
      defaultValue: '--',
      sx: { flex: 2 },
    },
  ]

  if (ticketStatus === DT_STATUS_ENUM.COMPLETED) {
    fields.push({
      name: 'signature',
      label: 'Signature',
      dataTest: 'dispatcher-signature-test-id',
      fieldType: 'static',
      defaultValue: '--',
      sx: { flex: 2 },
    })
  }

  return (
    <>
      <Stack direction="row" alignItems="center" paddingBottom="4px" spacing="4px">
        <Typography variant="h6" color="textSecondary">
          {dispatchTicketData?.status === DT_STATUS_ENUM.COMPLETED ? 'Completion Information' : 'Failure Information'}
        </Typography>
        <Button
          style={{
            display: 'flex',
            alignSelf: 'flex-start',
            alignItems: 'space-between',
            justifyContent: 'top',
          }}
          startIcon={<EditIcon />}
          onClick={() => {
            dispatch(
              showFormModal({
                formModalName: 'dispatch-ticket-status-blade',
                formModalProps: {
                  type: dispatchTicketData.status,
                  dispatchTicketId: dispatchTicketData?.id,
                  startTime: dispatchTicketData?.startTime || new Date().toISOString(),
                },
              }),
            )
          }}
        >
          EDIT
        </Button>
        {dispatchTicketData.docs?.length > 0 && (
          <FileUploader
            viewOnly
            filesData={dispatchTicketData?.docs || []}
            tooltipMsg=""
            buttonText="VIEW ATTACHMENTS"
          />
        )}
      </Stack>
      <Stack padding={2} sx={{ backgroundColor, borderRadius: '8px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
          {fields.map(field => (
            <Box key={field.name} sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              {renderSingleField(field)}
            </Box>
          ))}
        </Box>
      </Stack>
    </>
  )
}

export default CompletionInfoFormRow
